import { useEffect, useRef, useState } from 'react'
import { Image, Modal, Pressable, Text, View } from 'react-native'
import { Check, Lock } from 'lucide-react-native'
import { Button, InlineAlert } from '@chillout/ui'
import { CASHIER_PIN_LENGTH, colors } from '@chillout/core'
import { images } from '../../../assets'
import { CodeEntryDisplay } from '../shared/CodeEntryDisplay'
import { Numpad } from '../shared/numpad'

interface ChangePinModalBaseProps {
  visible: boolean
  title: string
  subtitle: string
  modalAccessibilityLabel: string
  closeAccessibilityLabel: string
  onClose: () => void
}

interface ChangePinEntryModalProps extends ChangePinModalBaseProps {
  mode: 'entry'
  autoConfirm?: boolean
  errorMessage?: string | null
  isLoading?: boolean
  getCodeAccessibilityLabel: (entered: number, total: number) => string
  onInputChange?: () => void
  onConfirm: (pin: string) => void
}

interface ChangePinSuccessModalProps extends ChangePinModalBaseProps {
  mode: 'success'
  doneLabel: string
  imageAccessibilityLabel: string
}

type ChangePinModalProps = ChangePinEntryModalProps | ChangePinSuccessModalProps

export function ChangePinModal(props: ChangePinModalProps) {
  const [digits, setDigits] = useState<string[]>([])
  const canConfirm = digits.length === CASHIER_PIN_LENGTH
  const onConfirmRef = useRef(props.mode === 'entry' ? props.onConfirm : null)
  onConfirmRef.current = props.mode === 'entry' ? props.onConfirm : null
  const autoConfirm = props.mode === 'entry' ? props.autoConfirm : undefined

  useEffect(() => {
    if (!autoConfirm) return
    if (digits.length !== CASHIER_PIN_LENGTH) return
    const pin = digits.join('')
    const timer = setTimeout(() => {
      setDigits([])
      onConfirmRef.current?.(pin)
    }, 300)
    return () => clearTimeout(timer)
  }, [digits, autoConfirm])

  const handleDigit = (digit: string) => {
    if (props.mode === 'entry') {
      props.onInputChange?.()
    }
    setDigits((current) => {
      if (current.length >= CASHIER_PIN_LENGTH) return current
      return [...current, digit]
    })
  }

  const handleDelete = () => {
    if (props.mode === 'entry') {
      props.onInputChange?.()
    }
    setDigits((current) => current.slice(0, -1))
  }

  const handleClose = () => {
    setDigits([])
    props.onClose()
  }

  const handleConfirm = () => {
    if (props.mode !== 'entry' || !canConfirm || props.isLoading) return
    const pin = digits.join('')
    setDigits([])
    props.onConfirm(pin)
  }

  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable
        className="flex-1 items-center justify-center bg-scrim px-6"
        onPress={handleClose}
        accessibilityLabel={props.closeAccessibilityLabel}
      >
        <Pressable
          className="w-full max-w-[360px] items-center rounded-xl bg-canvas-card px-8 py-8 shadow-md"
          onPress={(event) => event.stopPropagation()}
          accessibilityViewIsModal
          accessibilityLabel={props.modalAccessibilityLabel}
        >
          {props.mode === 'success' ? (
            <SuccessContent {...props} />
          ) : (
            <EntryContent
              {...props}
              digits={digits}
              canConfirm={canConfirm}
              onDigit={handleDigit}
              onDelete={handleDelete}
              onConfirm={handleConfirm}
            />
          )}
        </Pressable>
      </Pressable>
    </Modal>
  )
}

interface EntryContentProps extends ChangePinEntryModalProps {
  digits: string[]
  canConfirm: boolean
  errorMessage?: string | null
  isLoading?: boolean
  onDigit: (digit: string) => void
  onDelete: () => void
  onConfirm: () => void
}

function EntryContent({
  title,
  subtitle,
  digits,
  canConfirm,
  errorMessage,
  isLoading = false,
  autoConfirm = false,
  getCodeAccessibilityLabel,
  onDigit,
  onDelete,
  onConfirm,
}: EntryContentProps) {
  return (
    <>
      <View className="h-14 w-14 items-center justify-center rounded-full bg-border">
        <Lock size={28} color={colors.text.primary} strokeWidth={1.8} />
      </View>
      <Text className="mt-6 text-center text-xl font-semibold text-primary">{title}</Text>
      <Text className="mt-2 text-center text-base lg:text-lg font-normal text-secondary">
        {subtitle}
      </Text>
      <View className="mt-6 w-full">
        <CodeEntryDisplay
          digits={digits}
          length={CASHIER_PIN_LENGTH}
          hidden
          accessibilityLabel={getCodeAccessibilityLabel(digits.length, CASHIER_PIN_LENGTH)}
        />
      </View>
      {errorMessage ? (
        <View className="mt-4 w-full">
          <InlineAlert message={errorMessage} />
        </View>
      ) : null}
      <View className="mt-8 w-full">
        <Numpad
          onDigit={onDigit}
          onDelete={onDelete}
          onConfirm={onConfirm}
          canConfirm={canConfirm}
          isLoading={isLoading || (autoConfirm && canConfirm)}
        />
      </View>
    </>
  )
}

function SuccessContent({
  title,
  subtitle,
  doneLabel,
  imageAccessibilityLabel,
  onClose,
}: ChangePinSuccessModalProps) {
  return (
    <>
      <View className="h-16 w-16 items-center justify-center rounded-full bg-success/10">
        <Check size={34} color={colors.success} strokeWidth={2} />
      </View>
      <Text className="mt-6 text-center text-xl font-semibold text-primary">{title}</Text>
      <Text className="mt-2 text-center text-base lg:text-lg font-normal text-secondary">
        {subtitle}
      </Text>
      <Image
        source={images.changePinSuccess}
        style={{ width: 190, height: 150, marginTop: 32 }}
        resizeMode="contain"
        accessibilityLabel={imageAccessibilityLabel}
      />
      <Button
        label={doneLabel}
        onPress={onClose}
        accessibilityLabel={doneLabel}
        className="mt-10"
      />
    </>
  )
}
